import { ReactNode, useEffect, useMemo, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import {
  CheckCircle as CompletedStepIcon,
  CoinsSwapWhite as PriceActiveIcon,
  CoinSwap as PriceIcon,
  ErrorTriangle,
  LayersThree,
  Minimize as MinimizeIcon,
  ParagraphBlack as DetailsIcon,
  ParagraphWhite as DetailsActiveIcon,
  Rocket as ReviewIcon,
  RocketWhite as ReviewActiveIcon,
  Upload as UploadIcon,
  UploadWhite as UploadActiveIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import VerticalSidebarStepProgress from "@hl/base-components/lib/progress/VerticalSidebarStepProgress";
import {
  UploadStatus,
  useUploadManager,
} from "@hl/base-components/lib/upload/context";
import { ampli } from "@hl/shared-features/lib/ampli";
import useBeforeunload from "@hl/shared-features/lib/hooks/useBeforeunload";
import { usePrompt } from "@hl/shared-features/lib/hooks/usePrompt";
import {
  isTestnetNetwork,
  networkLookup,
} from "@hl/shared-features/lib/utils/blockExplorer";
import {
  ActionIcon,
  AppShell,
  Box,
  Button,
  createStyles,
  Flex,
  MantineTheme,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { Link, useParams } from "react-router-dom";
import { SECONDARY_COLOR } from "~/../../base-components/lib/theme/button";
import { AppSEO } from "~/../../shared-features/lib/features/layout/AppSEO";

import {
  _CollectionType,
  CapturePreviewTriggerType,
  Contract2,
  DraftProgress,
  PriceType,
  SupportedMediaType,
  TokenDistributionType,
} from "apollo/graphql.generated";
import { ReactComponent as ArrowLeftIcon } from "assets/icons/arrow-left.svg";
import { ReactComponent as PreviewIcon } from "assets/icons/preview.svg";
import { ReactComponent as VariantsIcon } from "assets/icons/variants.svg";
import { TOOLS_URL } from "config";
import { fileTypeFromExtension } from "utils/media/file";
import SizeAndOutputForm from "~/features/collections/forms/CreateCollectionMultistepForm/CodeGenerativeArt/SizeAndOutputForm";
import UploadCollectionAndMetadata from "~/features/collections/forms/CreateCollectionMultistepForm/UploadCollectionAndMetadata";
import { useCollectionDraftQuery } from "~/features/collections/queries/draft.graphql.generated";
import { AppFooter } from "~/features/layout/Footer";
import { AppHeader } from "~/features/layout/Header";

import { useGetContractsByUserQuery } from "../../queries/contracts.graphql.generated";
import QuickDropForm from "../QuickDropForm/QuickDropForm";

import { CodeGePreviewImage } from "./CodeGenerativeArt/PreviewImage";
import { CodeGenTestScript } from "./CodeGenerativeArt/TestScript";
import {
  CodeGenZipUpload,
  isCodeGenZipInvalid,
} from "./CodeGenerativeArt/ZipUpload";
import CollectionDetailsForm from "./CollectionDetailsForm";
import CollectionPriceForm from "./CollectionPriceForm";
import ReviewAndDeploy from "./ReviewAndDeploy";
import SelectImageAndMetadataForm from "./SelectImageAndMetadataForm";
import SelectTokenType from "./SelectTokenType";
import {
  CollectionUseForm,
  CreateCollectionForm,
  CreateCollectionState,
  getStepByType,
  getValidationPathMapping,
  pathMatchRegex,
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "./context";

type FlowStep = {
  label: string;
  hidden: boolean;
  type: DraftProgress;
  icon: {
    unfinished: ReactNode;
    active: ReactNode;
    finished: ReactNode;
  };
  node?: ReactNode;
  isDisabled?: (
    formValues: CreateCollectionForm,
    state: CreateCollectionState
  ) => boolean;
};

export const advanceInputStyles = (theme: MantineTheme) => ({
  border: `1px solid ${theme.colors.divider[0]}`,
  borderRadius: "6px",
});

const useStyles = createStyles((theme) => ({
  closeExpandBtn: {
    position: "absolute",
    right: 10,
    top: 10,
    width: 36,
    height: 36,
    boxShadow: theme.shadows.sm,
    borderRadius: 6,
  },
}));
const CreateCollectionMultistepForm = () => {
  let { collectionType: collectionTypeParam } = useParams();
  const { collectionId } = useParams();
  const { classes } = useStyles();
  const { isMobile } = useMantineMediaQueries();

  const { dispatch, state } = useCreateCollectionState();
  const { currentStep, deploying, steps, series } = state;
  const { state: uploadState } = useUploadManager();

  const form = useCreateCollectionFormContext();
  const formValues = form.values;

  //@ts-ignore
  window.formValues = formValues;

  const [contracts, setContracts] = useState<Contract2[]>([]);

  // query for contracts here so that they are preloaded
  const { data: contractsData, loading: contractsLoading } =
    useGetContractsByUserQuery({
      fetchPolicy: "no-cache",
    });

  const { data: draftData, loading: draftLoading } = useCollectionDraftQuery({
    variables: {
      collectionDraftId: collectionId!,
      first: 10,
    },
    skip: !collectionId,
  });

  // Listens to the URL params and redirect to the first step of the type selected
  useEffect(() => {
    if (collectionTypeParam) {
      // Different editions are now handled in the same flow
      // so default to OpenEdition
      if (collectionTypeParam === "Edition") {
        collectionTypeParam = _CollectionType.OpenEdition;
      }

      createFormDraft(collectionTypeParam as _CollectionType, form);
      dispatch({
        type: "JUMP_TO_STEP",
        payload: {
          currentStep: 1,
          collectionType: collectionTypeParam as _CollectionType,
          form,
        },
      });
    }
  }, [collectionTypeParam]);

  useEffect(() => {
    if (contractsData?.getContractsByUser) {
      setContracts(contractsData?.getContractsByUser);
    }
  }, [contractsData]);

  useEffect(() => {
    const collectionDraft = draftData?.collectionDraft;
    if (collectionDraft) {
      const metadata = collectionDraft.metadata;
      let mediaType: SupportedMediaType | "zip" = SupportedMediaType.IMAGE;

      if (metadata) {
        mediaType =
          metadata.mediaTypeStr === "zip"
            ? "zip"
            : SupportedMediaType[
                metadata.mediaTypeStr as keyof typeof SupportedMediaType
              ];
      }

      if (draftData) {
        dispatch({
          type: "LOAD_DRAFT",
          payload: {
            collectionType: draftData.collectionDraft.collectionType,
            furthestStep:
              // Furthest step or first one
              getStepByType(
                collectionDraft.furthestStep,
                draftData.collectionDraft.collectionType
              ) || 1,
            draftId: collectionDraft.id,
            draftProgress: collectionDraft.furthestStep,
            unzipped: collectionDraft.metadata?.seriesS3Unzipped ?? false,
            uploadedTokens:
              collectionDraft.seriesTokens?.edges.map((token) => {
                return {
                  imageUrl: token.imageUrl,
                  name: token.name,
                  tokenId: token.tokenId,
                  ...token.attributes.reduce((acc, curr) => {
                    acc[curr.name] = curr.value;
                    return acc;
                  }, {} as Record<string, string>),
                };
              }) || [],
          },
        });
      }
      const genDetails =
        collectionDraft.collectionDraft?.generativeDetails ??
        formValues.generativeDetails;
      const loadData: CreateCollectionForm = {
        collectionType: collectionDraft.collectionType,
        gateId: collectionDraft.gateId,
        metadata: {
          ...formValues.metadata,
          generateNameTrait:
            collectionDraft.metadata?.generateNameTrait ?? undefined,
          mediaUrl:
            collectionDraft.metadata?.mediaUrl || formValues.metadata.mediaUrl,
          mediaFiles:
            collectionDraft?.metadata?.mediaFiles ??
            formValues.metadata.mediaFiles,
          coverMediaUrl:
            collectionDraft.metadata?.coverMediaUrl ||
            formValues.metadata.coverMediaUrl,
          mediaType,
          traits: collectionDraft.metadata?.traits
            ? convertTraits(collectionDraft.metadata.traits)
            : {},
          seriesSize:
            collectionDraft.seriesTokens?.pageInfo.totalCount ||
            formValues.metadata.seriesSize,
          reveal:
            collectionDraft.metadata?.reveal ?? formValues.metadata.reveal,
          previewImages:
            collectionDraft.collectionDraft?.seriesDetails?.mintPreviewImageUrls?.map(
              (url: string, idx) => ({
                url,
                type: fileTypeFromExtension(url),
                name: `Image ${idx + 1}`,
              })
            ) || [],
          showAllMetadata:
            !!collectionDraft.collectionDraft?.seriesDetails?.showAllMetadata,
        },
        collection: {
          ...formValues.collection,
          name:
            collectionDraft.collectionDraft?.name || formValues.collection.name,
          description:
            collectionDraft.collectionDraft?.description ||
            formValues.collection.description,
          chainId:
            collectionDraft.contractDraft?.chainId?.toString() ||
            contracts
              .find((value) => value.id === collectionDraft.contractId)
              ?.chainId.toString() ||
            formValues.collection.chainId,
          size:
            collectionDraft.collectionDraft?.size || formValues.collection.size,
        },
        contract: collectionDraft.contractId
          ? {
              type: "existing",
              data: {
                contractId: collectionDraft.contractId,
              },
            }
          : {
              //TODO: Reuse code from contract page
              type: "new",
              edited: !!collectionDraft.contractDraft,
              data: {
                ...collectionDraft.contractDraft,
                name:
                  collectionDraft.contractDraft?.name ||
                  collectionDraft.collectionDraft?.name ||
                  "",
                symbol: collectionDraft.contractDraft?.symbol || "CNTRCT",
                usingOpenseaBlocklist:
                  !!collectionDraft.contractDraft?.usingOpenseaBlocklist,
                collectionLogoImage:
                  collectionDraft.contractDraft?.collectionLogoImage ??
                  collectionDraft.metadata?.mediaUrl ??
                  "",
              },
            },
        withoutMintVector: !collectionDraft.distributionDraft,
        distribution: {
          ...formValues.distribution,
          type:
            collectionDraft.distributionDraft?.type ||
            formValues.distribution.type,
          price: collectionDraft.distributionDraft?.price
            ? parseFloat(collectionDraft.distributionDraft.price)
            : formValues.distribution.price,
          currency:
            collectionDraft.distributionDraft?.currency ||
            formValues.distribution.currency,
          saleDateFrom: collectionDraft.distributionDraft?.saleDateFrom
            ? new Date(collectionDraft.distributionDraft.saleDateFrom)
            : formValues.distribution.saleDateFrom,
          saleDateTo: collectionDraft.distributionDraft?.saleDateTo
            ? new Date(collectionDraft.distributionDraft?.saleDateTo)
            : collectionDraft.distributionDraft?.saleDateTo === undefined
            ? formValues.distribution.saleDateTo
            : null,
          isDirectMint:
            collectionDraft.distributionDraft?.isDirectMint ?? false,
          priceType:
            collectionDraft.distributionDraft?.priceType ||
            formValues.distribution.priceType,
          priceDropInterval:
            collectionDraft.distributionDraft?.priceDropInterval?.map(
              (period) => ({ unit: period.unit, value: period.value })
            ) || formValues.distribution.priceDropInterval,
          scheduledPrices:
            collectionDraft.distributionDraft?.scheduledPrices ||
            formValues.distribution.scheduledPrices,
          isFreePrice:
            collectionDraft.distributionDraft?.price &&
            collectionDraft.distributionDraft?.priceType
              ? collectionDraft.distributionDraft?.priceType ===
                  PriceType.Fixed &&
                parseFloat(collectionDraft.distributionDraft.price) === 0
              : formValues.distribution.isFreePrice,
          minBid: collectionDraft.distributionDraft?.minBid
            ? parseFloat(collectionDraft.distributionDraft?.minBid)
            : formValues.distribution.minBid,
          rebateWinningBids:
            collectionDraft.distributionDraft?.rebateWinningBids ||
            formValues.distribution.rebateWinningBids,
          sponsoredMintsEnabled:
            collectionDraft.distributionDraft?.sponsoredMintsEnabled ||
            formValues.distribution.sponsoredMintsEnabled,
        },
        moduleSettings: {
          ...formValues.moduleSettings,
          shouldLimitNumberOfMintPerWallet:
            !!collectionDraft.moduleSettings.limitedMints?.limitPerWallet,
          limitPerWallet:
            collectionDraft.moduleSettings.limitedMints?.limitPerWallet ||
            formValues.moduleSettings.limitPerWallet,
          nonTransferable:
            collectionDraft.moduleSettings.nonTransferable?.active ||
            formValues.moduleSettings.nonTransferable,
          royalty: {
            ...formValues.moduleSettings.royalty,
            royaltyAmount:
              (collectionDraft.moduleSettings.royalty?.royaltyAmount || 0) /
                100 || formValues.moduleSettings.royalty.royaltyAmount,
            payoutEnsName:
              collectionDraft.moduleSettings.royalty?.payoutEnsName ||
              formValues.moduleSettings.royalty.payoutEnsName,
            payoutEoa:
              collectionDraft.moduleSettings.royalty?.payoutEoa ||
              formValues.moduleSettings.royalty.payoutEoa,
            address:
              collectionDraft.moduleSettings.royalty?.payoutEnsName ||
              collectionDraft.moduleSettings.royalty?.payoutEoa ||
              null,
          },
          customPaymentRecipient: {
            ...formValues.moduleSettings.customPaymentRecipient,
            ensName:
              collectionDraft.moduleSettings.customPaymentRecipient?.ensName ||
              formValues.moduleSettings.customPaymentRecipient.ensName,
            eoa:
              collectionDraft.moduleSettings.customPaymentRecipient?.eoa ||
              formValues.moduleSettings.customPaymentRecipient.eoa,
            address:
              collectionDraft.moduleSettings.customPaymentRecipient?.ensName ||
              collectionDraft.moduleSettings.customPaymentRecipient?.eoa ||
              null,
          },
        },
        generativeDetails: genDetails
          ? {
              captureSettings: {
                trigger:
                  genDetails.captureSettings?.trigger ??
                  CapturePreviewTriggerType.Delay,
                delay: genDetails.captureSettings?.delay ?? 3,
                selector: genDetails.captureSettings?.selector || undefined,
                selectorType:
                  genDetails.captureSettings?.selectorType || undefined,
                viewPort: {
                  width: genDetails.captureSettings?.viewPort?.width ?? 1000,
                  height: genDetails.captureSettings?.viewPort?.height ?? 1000,
                },
              },
              curateOutput: genDetails.curateOutput,
              isGPURendering: genDetails.isGPURendering,
              allowedHashes: genDetails.allowedHashes,
              editionType: genDetails.editionType,
            }
          : undefined,
      };

      form.setValues(loadData);
      // Resets the form initial state to be the loaded data, so only further changes will trigger save/alert.
      form.resetDirty(loadData);
      form.validate();
    }
  }, [draftData, contracts]);

  /**
   * Callbacks for Series uploads.
   * This effect exists here as the upload can happen in background
   * while the user move through other steps.
   */
  useEffect(() => {
    // Updates the form with the successfully uploaded file data.
    if (uploadState.status === UploadStatus.FINISHED) {
      form.setFieldValue("metadata.mediaType", "zip");
      form.setFieldValue("metadata.mediaUrl", series.newMediaUrl);
      form.setFieldValue("metadata.seriesSize", series.newTokenImageCount);
      if (formValues.collectionType === _CollectionType.Series) {
        form.setFieldValue("collection.size", series.newTokenImageCount);
      }
    }

    // Cleans local zip file tokens when upload errors out.
    if (
      uploadState.status === UploadStatus.ERROR &&
      (series.newTokens.length ||
        series.newMediaUrl ||
        series.newTokenImageCount)
    ) {
      dispatch({
        type: "UPDATE_SERIES",
        payload: {
          newTokens: [],
          newMediaUrl: null,
          newTokenImageCount: null,
        },
      });
    }
  }, [uploadState, series]);

  const shouldAlert =
    (form.isDirty() || uploadState.status === UploadStatus.IN_PROGRESS) &&
    !deploying;

  usePrompt("Changes you made may not be saved.", shouldAlert);
  useBeforeunload((event) => {
    if (shouldAlert) {
      event.preventDefault();
    }
  });
  const isGenSeries =
    formValues.collectionType === _CollectionType.GenerativeSeries;
  const isSeries = formValues.collectionType === _CollectionType.Series;
  const isEdition = !isGenSeries && !isSeries;
  const theme = useMantineTheme();

  const finished = (
    <CompletedStepIcon
      width={20}
      height={20}
      color={theme.colors.affirmativeStatus[0]}
    />
  );

  const baseSteps = useMemo((): FlowStep[] => {
    if (isGenSeries) {
      return [
        {
          label: "Select token type",
          hidden: true,
          type: DraftProgress.SELECTED_TYPE,
          icon: {
            unfinished: null,
            active: null,
            finished: null,
          },
        },
        {
          label: "Upload assets",
          hidden: false,
          type: DraftProgress.ADDED_METADATA,
          icon: {
            unfinished: <UploadIcon width={20} height={20} />,
            active: <UploadIcon width={20} height={20} />,
            finished,
          },
          node: <CodeGenZipUpload />,
        },
        {
          label: "Test script",
          hidden: false,
          type: DraftProgress.TESTED_GEN_SCRIPT,
          icon: {
            unfinished: <VariantsIcon width={20} height={20} />,
            active: <VariantsIcon width={20} height={20} />,
            finished,
          },
          node: <CodeGenTestScript />,
          isDisabled: isCodeGenZipInvalid,
        },
        {
          label: "Size and output",
          hidden: false,
          type: DraftProgress.ADDED_GEN_SIZE,
          icon: {
            unfinished: <LayersThree width={20} height={20} />,
            active: <LayersThree width={20} height={20} />,
            finished,
          },
          node: <SizeAndOutputForm />,
          isDisabled: isCodeGenZipInvalid,
        },
        {
          label: "Preview images",
          hidden: false,
          type: DraftProgress.ADDED_GEN_PREVIEW,
          icon: {
            unfinished: <PreviewIcon width={20} height={20} />,
            active: <PreviewIcon width={20} height={20} />,
            finished,
          },
          node: <CodeGePreviewImage />,
          isDisabled: isCodeGenZipInvalid,
        },
        {
          label: "Series details",
          hidden: false,
          type: DraftProgress.ADDED_DETAILS,
          icon: {
            unfinished: <DetailsIcon width={20} height={20} />,
            active: <DetailsActiveIcon width={20} height={20} />,
            finished,
          },
          node: (
            <CollectionDetailsForm
              contracts={contracts ?? []}
              title="Series details"
            />
          ),
          isDisabled: isCodeGenZipInvalid,
        },
        {
          label: "Mint details",
          hidden: false,
          type: DraftProgress.ADDED_DISTRIBUTION,
          icon: {
            unfinished: <PriceIcon width={20} height={20} />,
            active: <PriceActiveIcon width={20} height={20} />,
            finished,
          },
          node: <CollectionPriceForm contracts={contracts ?? []} />,
          isDisabled: isCodeGenZipInvalid,
        },
        {
          label: "Review and deploy",
          hidden: false,
          type: DraftProgress.FINISHED,
          icon: {
            unfinished: <ReviewIcon width={20} height={20} />,
            active: <ReviewActiveIcon width={20} height={20} />,
            finished,
          },
          node: (
            <ReviewAndDeploy
              handleSubmit={() => {
                dispatch({
                  type: "DEPLOY_COLLECTION",
                });
              }}
              contracts={contracts ?? []}
            />
          ),
          isDisabled: isCodeGenZipInvalid,
        },
      ];
    }

    if (isSeries) {
      return [
        {
          label: "Select token type",
          hidden: true,
          type: DraftProgress.SELECTED_TYPE,
          icon: {
            unfinished: null,
            active: null,
            finished: null,
          },
        },
        {
          label:
            formValues.collectionType === _CollectionType.OneOfOne
              ? "Upload artwork"
              : formValues.collectionType === _CollectionType.Series
              ? "Upload series"
              : "Upload collection",
          hidden: false,
          type: DraftProgress.ADDED_METADATA,
          icon: {
            unfinished: <UploadIcon width={20} height={20} />,
            active: <UploadActiveIcon width={20} height={20} />,
            finished,
          },
          node:
            formValues.collectionType === _CollectionType.Series ? (
              <UploadCollectionAndMetadata />
            ) : (
              <SelectImageAndMetadataForm />
            ),
        },
        {
          label: "Collection details",
          hidden: false,
          type: DraftProgress.ADDED_DETAILS,
          icon: {
            unfinished: <DetailsIcon width={20} height={20} />,
            active: <DetailsActiveIcon width={20} height={20} />,
            finished,
          },
          node: <CollectionDetailsForm contracts={contracts ?? []} />,
        },
        {
          label: "Price and distribution",
          hidden: false,
          type: DraftProgress.ADDED_DISTRIBUTION,
          icon: {
            unfinished: <PriceIcon width={20} height={20} />,
            active: <PriceActiveIcon width={20} height={20} />,
            finished,
          },
          node: <CollectionPriceForm contracts={contracts ?? []} />,
        },
        {
          label: "Review and deploy",
          hidden: false,
          type: DraftProgress.FINISHED,
          icon: {
            unfinished: <ReviewIcon width={20} height={20} />,
            active: <ReviewActiveIcon width={20} height={20} />,
            finished,
          },
          node: (
            <ReviewAndDeploy
              handleSubmit={() => {
                dispatch({
                  type: "DEPLOY_COLLECTION",
                });
              }}
              contracts={contracts ?? []}
            />
          ),
        },
      ];
    }

    // Quick drop
    // if (isEdition)
    return [
      {
        label: "Select token type",
        hidden: true,
        type: DraftProgress.SELECTED_TYPE,
        icon: {
          unfinished: null,
          active: null,
          finished: null,
        },
      },
      {
        label: "Edition",
        hidden: false,
        type: DraftProgress.ADDED_DISTRIBUTION,
        icon: {
          unfinished: <UploadIcon width={20} height={20} />,
          active: <UploadActiveIcon width={20} height={20} />,
          finished,
        },
        node: <QuickDropForm contracts={contracts ?? []} />,
      },
    ];
  }, [contracts, formValues.collectionType]);

  const collectionType = formValues.collectionType;
  const draftId = draftData?.collectionDraft?.id;

  useEffect(() => {
    const values = form.values;
    const step = baseSteps[currentStep];
    const isLastStep = baseSteps.length - 1 == currentStep;
    const network = networkLookup(values.collection.chainId);
    if (ampli.isLoaded) {
      ampli.deployCollectionStep({
        stepNum: currentStep,
        stepTitle: step.label,
        blockchain: network?.type,
        isTestnet: isTestnetNetwork(network.type),
        collectionType,
        projectName: form.values.collection.name ?? "",
        isLastStep,
        ...(isLastStep && {
          errorsCount: Object.keys(form.errors).length,
          errors: Object.entries(form.errors).map((k, v) => `${k}: ${v}`),
        }),
        draftId,
      });
    }

    // save draft clicked
  }, [currentStep, baseSteps, collectionType, form, draftId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep, state.fullWidthMode]);

  const collectionTypeTitle = (
    collectionType: _CollectionType | null
  ): string => {
    if (collectionType === _CollectionType.Series) {
      return "Create a series";
    } else if (collectionType === _CollectionType.GenerativeSeries) {
      return "Create a generative series";
    } else if (collectionType === _CollectionType.LimitedEdition) {
      return "Create a limited edition";
    } else if (collectionType === _CollectionType.OpenEdition) {
      return "Create an open edition";
    } else if (collectionType === _CollectionType.OneOfOne) {
      return "Create a one of one token";
    } else {
      return "Create a collection";
    }
  };

  if (contractsLoading || draftLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      <AppSEO title="Create a collection" />
      <AppShell
        header={
          state.fullWidthMode ? (
            state.fullWidthCloseBtn ? (
              <ActionIcon
                className={classes.closeExpandBtn}
                onClick={() => {
                  dispatch({
                    type: "SET_FULL_WIDTH",
                    payload: { enable: false },
                  });
                }}
              >
                <MinimizeIcon />
              </ActionIcon>
            ) : undefined
          ) : (
            <AppHeader
              title={
                isMobile ? null : currentStep === 0 ? (
                  <Text fw={500}>Create a collection</Text>
                ) : (
                  <Text fw={500}>{collectionTypeTitle(collectionType)}</Text>
                )
              }
              leftButtons={
                <Link to={TOOLS_URL.home}>
                  <Button
                    color={SECONDARY_COLOR}
                    leftIcon={<ArrowLeftIcon height={12} />}
                  >
                    Back
                  </Button>
                </Link>
              }
            />
          )
        }
        footer={<AppFooter />}
      >
        <Box w={"100%"} mb={116} px={20}>
          {currentStep === 0 && <SelectTokenType />}
          {currentStep > 0 &&
            (isEdition ? (
              <>
                <Box maw={260 + 1000} mx={"auto"}>
                  {baseSteps[currentStep].node}
                </Box>
              </>
            ) : (
              <>
                <Flex gap={40} wrap="nowrap" align="flex-start">
                  {!state.fullWidthMode && (
                    <Box
                      miw={220}
                      maw={220}
                      style={{
                        position: "fixed",
                      }}
                    >
                      <VerticalSidebarStepProgress
                        align="stretch"
                        steps={baseSteps.map((baseStep, i) => {
                          const stepState = steps[i];
                          const validationPaths = getValidationPathMapping(
                            collectionType,
                            i
                          );

                          const errorPaths = Object.keys(form.errors);
                          // Creates a regex to match the error paths with the possibly partial validation paths
                          // e.g. an error path "contract.data.collectionLogoImage" will match a validation path "contract.data"
                          const validationPathRegex = validationPaths
                            ? pathMatchRegex(validationPaths)
                            : undefined;
                          const validation = validationPathRegex
                            ? errorPaths.some((path) =>
                                path.match(validationPathRegex)
                              ) ||
                              (i ===
                                getStepByType(
                                  DraftProgress.FINISHED,
                                  collectionType
                                ) &&
                                errorPaths.length)
                              ? "error"
                              : "valid"
                            : "not-validated";

                          return {
                            ...baseStep,
                            active: currentStep === i,
                            finished:
                              currentStep !== i &&
                              stepState.visited &&
                              validation !== "not-validated",
                            icon: {
                              ...baseStep.icon,
                              ...(stepState.visited &&
                                validation === "error" && {
                                  finished: (
                                    <ErrorTriangle
                                      color={theme.colors.errorStatus[0]}
                                    />
                                  ),
                                }),
                            },
                            disabled: baseStep.isDisabled
                              ? baseStep.isDisabled(formValues, state)
                              : undefined,
                            onClick: async () => {
                              dispatch({
                                type: "JUMP_TO_STEP",
                                payload: { currentStep: i, form },
                              });
                            },
                          };
                        })}
                      />
                    </Box>
                  )}
                  <Box
                    maw={260 + 1000}
                    mx={"auto"}
                    sx={{
                      // Avoid flex overflowing when right side is too long
                      minWidth: 0,
                      flex: 1,
                      paddingLeft: state.fullWidthMode ? 0 : 260,
                    }}
                  >
                    {baseSteps[currentStep].node}
                  </Box>
                </Flex>
              </>
            ))}
        </Box>
      </AppShell>
    </>
  );
};

const convertTraits = (
  traits: Array<{
    traitName: string;
    traitValue: string;
  }>
) =>
  traits.reduce(
    (
      acc: Record<string, string>,
      curr: {
        traitName: string;
        traitValue: string;
      }
    ) => {
      acc[curr.traitName] = curr.traitValue;
      return acc;
    },
    {}
  );

export const createFormDraft = (
  collectionType: _CollectionType,
  form: CollectionUseForm
) => {
  const formValues = form.values;
  const newValues: CreateCollectionForm = {
    ...form.values,
    collectionType,
    collection: {
      ...formValues.collection,
      size:
        collectionType === _CollectionType.OneOfOne
          ? 1
          : collectionType === _CollectionType.OpenEdition
          ? 0
          : collectionType === _CollectionType.Series
          ? formValues.metadata.seriesSize || 0
          : null,
    },
    distribution: {
      ...formValues.distribution,
      type: TokenDistributionType.Mint,
    },
  };

  form.setValues(newValues);
  form.resetDirty(newValues);
};

export default CreateCollectionMultistepForm;
